<script setup lang="ts">
import { computed, provide } from 'vue'
import type { BookPageConfig } from '../configs/types'
import BizPopupBookTOCFlipnovel from '@/components/BizPopupBookTOC.flipnovel.vue'
import { theme as brandTheme } from '@/config/brands'
import { formatCompactNumber } from '@/utils'
import { t } from '@/i18n/utils'

interface Props {
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
  currentContentId?: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
  (event: 'audio'): void
  /** 上滑提示点击（真正的跳章逻辑仍在 index.vue 的 handlePullUp） */
  (event: 'pull-up'): void
}>()

/** 设计稿：第一章预览显示 3 段 */
const PREVIEW_PARAGRAPH_COUNT = 3

const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})

// ==================== 设计 token（优先取页面配置，兜底写死设计值） ====================
const tk = computed(() => {
  const th = props.config.theme
  return {
    hero: th.heroGradient || 'linear-gradient(170deg, #FDE8F1 0%, #EDE4FD 100%)',
    cardShadow: th.cardShadow || '0 4px 16px rgba(124, 77, 232, 0.08)',
    accent: th.accentColor || '#7C4DE8',
    body: th.bodyColor || '#4A3D52',
    bottomShadow: th.bottomBarShadow || '0 -4px 16px rgba(124, 77, 232, 0.08)',
    cta: th.startReadingBg || brandTheme.primaryGradient || 'linear-gradient(90deg, #F0509B 0%, #9B5CF0 100%)',
    pink: brandTheme.primaryColor || '#F0509B',
    coverShadow: th.coverShadow || '0 10px 28px rgba(124, 77, 232, 0.2)',
    divider: th.cardDividerColor || '#F2EAF4',
  }
})

const chipBgs = computed(() => {
  const list = props.config.theme.chipBgs
  return list && list.length ? list : ['#FDEFF6', '#F3EDFE']
})

/** 标签 chip：粉 / 紫 soft 交替 */
function chipStyle(index: number) {
  const bgs = chipBgs.value
  const colors = [tk.value.pink, tk.value.accent]
  return {
    background: bgs[index % bgs.length],
    color: colors[index % colors.length],
  }
}

// ==================== 展示数据（全部来自已有 props，无任何新增请求） ====================
const tagList = computed(() =>
  (props.book.tagList || []).filter((tag): tag is { id: string; name: string } => !!tag.name),
)
const chapterCount = computed(() => props.book.chapterNum || props.book.lastChapter?.sequenceNo || 0)
const viewCount = computed(() => formatCompactNumber(props.book.viewUsers))
const previewParagraphs = computed(() => props.paragraphs.slice(0, PREVIEW_PARAGRAPH_COUNT))

/**
 * 目录卡预览行：只用详情接口已返回的 firstChapter / lastChapter，
 * 不新增任何接口调用（按 99-final-check 第七节裁决）。
 */
interface CatalogueRow { id: string; name: string; badge: 'free' | 'latest' }
const catalogueRows = computed<CatalogueRow[]>(() => {
  const rows: CatalogueRow[] = []
  const first = props.book.firstChapter
  const last = props.book.lastChapter
  if (first?.chapterName)
    rows.push({ id: first.id, name: first.chapterName, badge: 'free' })
  if (last?.chapterName && last.id !== first?.id)
    rows.push({ id: last.id, name: last.chapterName, badge: 'latest' })
  return rows
})

// 目录弹窗按浅色主题渲染
provide('theme', { background: '#FFFFFF' })
</script>

<template>
  <div class="nm-book bg-n-bg-global">
    <!-- ==================== Hero ==================== -->
    <div class="nm-hero px-16px pt-18px pb-24px" :class="{ 'mt-60px': bannerShow }">
      <!-- 导航行（设计稿右上角 share 图标按裁决移除，只留返回） -->
      <div class="flex items-center">
        <button type="button" class="nm-icon-btn text-n-text-1" aria-label="Back" @click="emit('back')">
          <i-lucide-chevron-left class="w-24px h-24px" />
        </button>
      </div>

      <!-- 封面 -->
      <img
        class="mt-6px mx-auto block w-124px h-166px rounded-14px object-cover"
        :style="{ boxShadow: tk.coverShadow }"
        :src="coverUrl"
        alt="Book Cover"
      >

      <!-- 书名 -->
      <h1 class="nm-title mx-auto mt-16px max-w-300px text-center text-20px text-n-text-1">
        {{ book.contentName }}
      </h1>

      <!-- meta：状态 · 作者 · 阅读人数 -->
      <div class="mt-8px flex flex-wrap items-center justify-center text-12px">
        <span class="mx-3px font-700" :style="{ color: tk.pink }">
          {{ book.finishStatus ? config.texts.completed : config.texts.ongoing }}
        </span>
        <span class="mx-3px text-n-text-3">·</span>
        <span class="mx-3px text-n-text-2 line-clamp-1 max-w-140px">{{ book.authorInfo?.author }}</span>
        <template v-if="book.viewUsers">
          <span class="mx-3px text-n-text-3">·</span>
          <span class="mx-3px inline-flex items-center text-n-text-2">
            <i-lucide-eye class="mr-4px w-14px h-14px" />
            {{ viewCount }}
          </span>
        </template>
      </div>
    </div>

    <!-- ==================== 白卡 1：标签 + 引言 ==================== -->
    <section class="nm-card mx-16px -mt-8px px-16px pt-14px pb-16px">
      <div v-if="tagList.length" class="nm-chip-row flex flex-wrap">
        <button
          v-for="(tag, index) in tagList"
          :key="tag.id"
          type="button"
          class="nm-chip mr-8px mb-8px"
          :style="chipStyle(index)"
          @click="emit('goto-tags', tag.id || '')"
        >
          {{ tag.name }}
        </button>
      </div>

      <i-lucide-quote class="mt-12px w-20px h-20px" :style="{ color: config.theme.quoteColor }" />

      <BasicExpandableText
        class="mt-6px"
        :text="book.introduce || ''"
        :line-clamp="4"
        text-class="nm-quote-text text-13px text-n-text-2"
        more-class="nm-more text-12px font-700 cursor-pointer"
        :more-text="t('ReadMore')"
        more-bg-color="#FFFFFF"
      />
    </section>

    <!-- ==================== 白卡 2：Catalogue ==================== -->
    <section class="nm-card mx-16px mt-12px px-16px py-14px">
      <div class="flex items-center justify-between cursor-pointer" @click="emit('open-toc')">
        <h2 class="nm-title text-17px text-n-text-1">
          {{ config.texts.catalogue }}
        </h2>
        <div class="flex items-center text-12px text-n-text-3">
          <span>{{ chapterCount }} {{ config.texts.chapters }}</span>
          <i-lucide-chevron-right class="ml-2px w-14px h-14px" />
        </div>
      </div>

      <ul v-if="catalogueRows.length" class="mt-10px">
        <li
          v-for="row in catalogueRows"
          :key="row.id"
          class="nm-chapter-row flex items-center justify-between cursor-pointer"
          @click="emit('open-toc')"
        >
          <span class="mr-12px min-w-0 flex-1 truncate text-13px text-n-text-2">{{ row.name }}</span>
          <span v-if="row.badge === 'free'" class="flex-shrink-0 text-11px font-700" :style="{ color: tk.accent }">
            {{ t('free') }}
          </span>
          <span v-else class="flex-shrink-0 text-11px text-n-text-3">
            {{ config.texts.latestChapter }}
          </span>
        </li>
      </ul>
    </section>

    <!-- ==================== 白卡 3：第一章预览 ==================== -->
    <section class="nm-card mx-16px mt-12px mb-100px px-16px pt-16px pb-18px">
      <!-- 引导语（getLeadSentence，保留） -->
      <div v-if="introduce" class="nm-serif mb-12px text-13px text-n-text-2">
        <p v-html="introduce" />
      </div>

      <h3 class="nm-serif text-17px font-700 text-n-text-1">
        {{ book.firstChapter?.chapterName }}
      </h3>

      <div class="mt-10px">
        <p
          v-for="(item, index) in previewParagraphs"
          :key="index"
          class="nm-serif mt-10px first:mt-0 text-13px"
          :style="{ color: tk.body }"
        >
          {{ item }}
        </p>
      </div>

      <!-- 上滑继续（只是提示 UI 挪到卡内，跳章逻辑仍在 index.vue） -->
      <div
        v-if="config.theme.swipeUpInline"
        class="mt-20px flex flex-col items-center cursor-pointer"
        :style="{ color: tk.accent }"
        @click="emit('pull-up')"
      >
        <i-lucide-chevrons-up class="w-20px h-20px animate-bounce" />
        <span class="mt-2px text-13px font-700">{{ t('SwipeUpToContinue') }}</span>
      </div>
    </section>

    <!-- ==================== 目录弹窗（复用现有组件与逻辑） ==================== -->
    <BizPopupBookTOCFlipnovel
      v-if="book && book.firstChapterContentUrl"
      v-model="popupTOCModel"
      :book="book"
      :content-id="book.id"
      :chapter-id="book.firstChapter?.id"
      :lock-icon-color="config.theme.tocLockColor"
      :style="{ '--toc-border-color': tk.divider }"
    />

    <!-- ==================== 底部操作条 ==================== -->
    <BasicFixedBottom class="!pb-0" :style="{ boxShadow: tk.bottomShadow }">
      <div class="nm-bottom-bar bg-n-bg-card">
        <div
          class="nm-shelf-btn flex flex-col items-center justify-center cursor-pointer"
          :class="{ 'opacity-50': shelfLoading }"
          :style="{ color: isInShelf ? tk.pink : undefined }"
          @click="emit('toggle-shelf')"
        >
          <i-lucide-bookmark-check v-if="isInShelf" class="w-22px h-22px pointer-events-none" />
          <i-lucide-bookmark-plus v-else class="w-22px h-22px pointer-events-none text-n-text-2" />
          <p class="mt-2px text-10px lh-12px" :class="{ 'text-n-text-2': !isInShelf }">
            {{ isInShelf ? t('BookshelfAdded') : t('Bookshelf') }}
          </p>
        </div>

        <a
          class="ml-14px flex-1 h-48px lh-48px rounded-24px text-center text-15px font-800 text-white cursor-pointer"
          :style="{ background: tk.cta }"
          @click="emit('read')"
        >
          {{ !book.readFlag ? config.texts.startReading : config.texts.continueReading }}
        </a>
      </div>
    </BasicFixedBottom>
  </div>
</template>

<style scoped>
.nm-book {
  overflow-x: hidden;
}

.nm-hero {
  background: v-bind('tk.hero');
}

.nm-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

/* Baloo 2：书名 / 区块标题（@font-face 在 htmls/novelmello.html 声明） */
.nm-title {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif);
  font-weight: 700;
  line-height: 1.25;
}

/* Lora：引导语 / 章节预览正文 */
.nm-serif {
  font-family: var(--nm-font-serif, 'Lora', Georgia, 'Times New Roman', serif);
  line-height: 1.7;
}

.nm-card {
  position: relative;
  border-radius: 20px;
  background: #fff;
  box-shadow: v-bind('tk.cardShadow');
}

/* chip 用 margin 拼接（规避 iOS 14.4 不支持 flex gap），底部负 margin 抵消最后一行间距 */
.nm-chip-row {
  margin-bottom: -8px;
}

.nm-chip {
  height: 22px;
  padding: 0 10px;
  border: 0;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;
}

.nm-chapter-row {
  height: 38px;
  border-bottom: 1px solid v-bind('tk.divider');
}

.nm-chapter-row:last-child {
  border-bottom: 0;
}

/* 引言行高 1.55（BasicExpandableText 的 text-class 透传） */
:deep(.nm-quote-text) {
  line-height: 1.55;
}

/* BasicExpandableText 的 line-clamp-N 是运行时拼接的类名，UnoCSS 扫不到，这里兜底一份 */
:deep(.line-clamp-4) {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

:deep(.nm-more) {
  color: v-bind('tk.accent');
}

.nm-bottom-bar {
  display: flex;
  align-items: center;
  padding: 8px 16px calc(8px + env(safe-area-inset-bottom));
}

.nm-shelf-btn {
  min-width: 52px;
}
</style>
